//src/components/layout/Topbar.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bell,
  BellRing,
  ChevronRight,
  ClipboardCheck,
  LogOut,
  ShieldAlert,
  Sun,
  Moon,
  Search,
  User,
} from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { listNotifications, markNotificationAsRead } from "@/services/notifications";
import type { NotificationItem } from "@/types/notifications";
import { toast } from "sonner";

const routeLabels: Record<string, string> = {
  "": "Dashboard",
  cadastros: "Cadastros",
  pessoas: "Pessoas",
  usuarios: "Usuários",
  grupos: "Grupos de Acesso",
  empresa: "Empresa",
  unidades: "Unidades",
  departamentos: "Departamentos",
  cargos: "Cargos",
  novo: "Novo",
  editar: "Editar",
  onboarding: "Onboarding",
  gro: "GRO",
  settings: "Configurações",
  questionnaires: "Questionários",
  campaigns: "Campanhas",
  resultados: "Resultados",
  results: "Resultados",
  dimensions: "Dimensões",
  "dimension-risk-rules": "Dimensões x Riscos",
  "worker-groups": "Grupos de Avaliação",
  "risk-inventories": "Inventários de Risco",
  inventarios: "Inventários de Risco",
  items: "Itens",
  "action-plans": "Planos de Ação",
  "action-plan-models": "Planos de Ação",
  execution: "Execução",
  "risk-suggestions": "Sugestões de Riscos",
  riscos: "Riscos",
  areas: "Áreas/Setores",
  inspecoes: "Inspeções",
  acoes: "Planos de Ação",
  documentos: "Documentos",
  treinamentos: "Treinamentos",
  relatorios: "Relatórios",
  "historico-ai": "Histórico de AI",
  notificacoes: "Notificações",
  financeiro: "Financeiro",
  assinatura: "Assinatura",
  faturas: "Faturas",
  pagamentos: "Pagamentos",
  nfse: "Nota Fiscal",
  configuracoes: "Configurações",
};

function formatSegmentLabel(segment: string) {
  return routeLabels[segment] || segment;
}

function formatRelativeDate(value: string) {
  const date = new Date(value);
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });

  if (Math.abs(diffMinutes) < 60) return rtf.format(diffMinutes, "minute");
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, "hour");
  const diffDays = Math.round(diffHours / 24);
  return rtf.format(diffDays, "day");
}

function notificationIcon(item: NotificationItem) {
  if (item.type === "campaign_low_engagement" || item.type === "campaign_closing_soon_pending_response") {
    return ShieldAlert;
  }
  if (item.type === "action_plan_item_assigned") {
    return ClipboardCheck;
  }
  return BellRing;
}

function notificationAccent(item: NotificationItem) {
  if (item.severity === "critical") return "border-l-red-500";
  if (item.severity === "warning") return "border-l-amber-500";
  return "border-l-blue-500";
}

export function Topbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const allSegments = location.pathname.split("/").filter(Boolean);
  const inventoryIdIndex = allSegments.findIndex(
    (segment) => uuidRegex.test(segment) && allSegments[allSegments.indexOf(segment) - 1] === "risk-inventories"
  );
  const inventoryId = inventoryIdIndex >= 0 ? allSegments[inventoryIdIndex] : null;

  const breadcrumbSegments = allSegments.reduce<Array<{ segment: string; label: string; path: string }>>((acc, segment, index) => {
    if (uuidRegex.test(segment)) return acc;

    const previous = allSegments[index - 1];
    const next = allSegments[index + 1];
    let path = `/${allSegments.slice(0, index + 1).join("/")}`;

    if (segment === "items" && inventoryId) {
      path = `/gro/risk-inventories/${inventoryId}`;
    }

    if (segment === "action-plans" && previous === "items" && inventoryId && next == null) {
      path = location.pathname;
    }

    acc.push({
      segment,
      label: formatSegmentLabel(segment),
      path,
    });

    return acc;
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      setLoadingNotifications(true);
      const data = await listNotifications({ page: 1, per_page: 5 });
      setNotifications(data.items);
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    let ignore = false;

    const safeLoadNotifications = async () => {
      try {
        setLoadingNotifications(true);
        const data = await listNotifications({ page: 1, per_page: 5 });

        if (ignore) return;

        setNotifications(data.items);
        setUnreadCount(data.unread_count);
      } catch (error) {
        if (!ignore) {
          console.error(error);
        }
      } finally {
        if (!ignore) {
          setLoadingNotifications(false);
        }
      }
    };

    const handleRefresh = () => {
      if (!ignore) {
        void safeLoadNotifications();
      }
    };

    void safeLoadNotifications();

    const interval = window.setInterval(() => {
      if (!ignore) {
        void safeLoadNotifications();
      }
    }, 60000);

    window.addEventListener("focus", handleRefresh);

    return () => {
      ignore = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", handleRefresh);
    };
  }, []);

  const accurateUnreadCount = useMemo(() => unreadCount, [unreadCount]);

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";

  const handleNotificationClick = async (notification: NotificationItem) => {
    try {
      if (!notification.is_read) {
        await markNotificationAsRead(notification.id);
        setNotifications((current) =>
          current.map((item) =>
            item.id === notification.id
              ? { ...item, is_read: true, read_at: new Date().toISOString() }
              : item
          )
        );
        setUnreadCount((current) => Math.max(0, current - 1));
      }

      if (!notification.route) {
        return;
      }

      const targetRoute = notification.route;

      if (
        notification.type === "campaign_available_to_answer" ||
        notification.type === "campaign_closing_soon_pending_response"
      ) {
        window.open(targetRoute, "_blank", "noopener,noreferrer");
        return;
      }

      navigate(targetRoute);
    } catch (error) {
      console.error(error);
      toast.error("Não foi possível abrir a notificação.");
    }
  };

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <SidebarTrigger className="shrink-0" />

      <Breadcrumb className="hidden md:flex">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Normex</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>

          {breadcrumbSegments.map(({ segment, label, path }, index) => {
            const isLast = index === breadcrumbSegments.length - 1;

            return (
              <BreadcrumbItem key={path}>
                <BreadcrumbSeparator>
                  <ChevronRight className="h-3.5 w-3.5" />
                </BreadcrumbSeparator>

                {isLast || segment === "gro" ? (
                  <span className="font-medium text-foreground">{label}</span>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={path}>{label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex-1" />

      <div className="hidden w-64 lg:block">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar..." className="h-9 bg-muted/50 pl-8" />
        </div>
      </div>

      <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-9 w-9">
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>

      <DropdownMenu
        open={notificationsOpen}
        onOpenChange={(nextOpen) => {
          setNotificationsOpen(nextOpen);
          if (nextOpen) {
            void loadNotifications();
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative h-9 w-9 rounded-full">
            <Bell className="h-4 w-4" />
            {accurateUnreadCount > 0 && (
              <Badge className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] text-destructive-foreground">
                {accurateUnreadCount > 9 ? "9+" : accurateUnreadCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-[380px] rounded-2xl p-0">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <p className="text-sm font-semibold">Notificações</p>
              <p className="text-xs text-muted-foreground">Acompanhe o que precisa da sua atenção.</p>
            </div>
            {accurateUnreadCount > 0 && <Badge variant="secondary">{accurateUnreadCount} novas</Badge>}
          </div>

          <div className="max-h-[360px] overflow-y-auto p-2">
            {loadingNotifications ? (
              <div className="space-y-2 p-2">
                {[0, 1, 2].map((item) => (
                  <div key={item} className="h-16 animate-pulse rounded-xl bg-muted/60" />
                ))}
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((notification) => {
                const Icon = notificationIcon(notification);
                return (
                  <DropdownMenuItem
                    key={notification.id}
                    className={`mb-2 flex cursor-pointer items-start gap-3 rounded-xl border border-transparent border-l-4 p-3 focus:bg-muted ${notificationAccent(notification)} ${!notification.is_read ? "bg-primary/5" : "bg-transparent"}`}
                    onClick={() => void handleNotificationClick(notification)}
                  >
                    <div className="mt-0.5 rounded-full bg-background p-2 shadow-sm">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className={`line-clamp-1 text-sm ${!notification.is_read ? "font-semibold text-foreground" : "font-medium text-foreground/80"}`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && <span className="ml-auto h-2.5 w-2.5 rounded-full bg-primary" />}
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{notification.message}</p>
                      <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                        <span>{formatRelativeDate(notification.created_at)}</span>
                        {notification.is_general ? <span>Geral</span> : <span>Para você</span>}
                      </div>
                    </div>
                  </DropdownMenuItem>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
                <BellRing className="h-10 w-10 text-muted-foreground/60" />
                <p className="text-sm font-medium">Nenhuma notificação por aqui</p>
                <p className="max-w-xs text-xs text-muted-foreground">
                  Quando houver algo importante para acompanhar, ele vai aparecer aqui.
                </p>
              </div>
            )}
          </div>

          <DropdownMenuSeparator />
          <div className="p-2">
            <Button
              variant="outline"
              className="h-10 w-full rounded-xl"
              onClick={() => navigate("/notificacoes")}
            >
              Ver todas
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-9 gap-2 px-2">
            <Avatar className="h-7 w-7">
              <AvatarFallback className="bg-primary text-xs text-primary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-medium md:inline-block">{user?.name}</span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => navigate("/configuracoes")}>
            <User className="mr-2 h-4 w-4" />
            Meu Perfil
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-destructive">
            <LogOut className="mr-2 h-4 w-4" />
            Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
